import { useCallback, useMemo, useState } from 'react';
import { acmgData } from './acmgData';

export type AcmgClassification =
  | 'Pathogenic'
  | 'Likely pathogenic'
  | 'Uncertain Significance'
  | 'Likely Benign'
  | 'Benign';

/** User-selectable strength for scoring (flexible interpretation). */
export type UserEvidenceStrength =
  | 'supporting'
  | 'moderate'
  | 'strong'
  | 'very_strong';

/** BA1-style standalone; only offered for BA* codes. */
export type BenignStandaloneStrength = 'standalone';

export type AppliedStrength = UserEvidenceStrength | BenignStandaloneStrength;

export type AcmgCriterion = {
  name: string;
  description: string;
  color?: string;
  bucket: 'pathogenic' | 'benign';
  /** Suggested default from ACMG code prefix (user may override). */
  defaultStrength: AppliedStrength;
};

type ClassificationCounts = {
  pvs: number;
  ps: number;
  pm: number;
  pp: number;
  ba: number;
  bs: number;
  bp: number;
};

const getDefaultStrengthForCode = (code: string): AppliedStrength => {
  if (code.startsWith('PVS')) return 'very_strong';
  if (code.startsWith('PS') || code.startsWith('BS')) return 'strong';
  if (code.startsWith('PM')) return 'moderate';
  if (code.startsWith('BA')) return 'standalone';
  return 'supporting';
};

const criteria = acmgData.criteria.map((item) => ({
  ...item,
  bucket: item.name.startsWith('B') ? 'benign' : 'pathogenic',
  defaultStrength: getDefaultStrengthForCode(item.name),
})) as AcmgCriterion[];

/** Weights from acmgData (incl. standalone for BA1). */
const criteriaWeights = acmgData.criteria_value[0] as {
  supporting: number;
  moderate: number;
  strong: number;
  very_strong: number;
  standalone: number;
};

/**
 * Point weight for selected strength. BA1 uses `standalone` from criteria_value (e.g. 8).
 */
export const getStrengthWeight = (strength: AppliedStrength): number => {
  if (strength === 'standalone') return criteriaWeights.standalone;
  return criteriaWeights[strength];
};

/**
 * D30-style score: pathogenic weights summed minus benign weights summed.
 */
export const getScoreFromSelections = (
  selections: Record<string, AppliedStrength>,
): number =>
  Object.entries(selections).reduce((sum, [code, strength]) => {
    const crit = criteria.find((c) => c.name === code);
    if (!crit) return sum;
    const w = getStrengthWeight(strength);
    return crit.bucket === 'pathogenic' ? sum + w : sum - w;
  }, 0);

/**
 * Matches spreadsheet IFS on D30 (wording aligned with ACMG Calculator.xlsx).
 */
export const computeClassificationFromScore = (d30: number): AcmgClassification => {
  if (d30 <= -7) return 'Benign';
  if (d30 < 0) return 'Likely Benign';
  if (d30 <= 5) return 'Uncertain Significance';
  if (d30 < 10) return 'Likely pathogenic';
  return 'Pathogenic';
};

type PathFamily = 'pvs' | 'ps' | 'pm' | 'pp';

const getPathFamily = (code: string): PathFamily | null => {
  if (code.startsWith('PVS')) return 'pvs';
  if (code.startsWith('PS')) return 'ps';
  if (code.startsWith('PM')) return 'pm';
  if (code.startsWith('PP')) return 'pp';
  return null;
};

/**
 * Maps user-chosen strength to PVS/PS/PM/PP counters (flexible lab interpretation).
 */
const pathStrengthToBucket: Record<
  PathFamily,
  Record<UserEvidenceStrength, keyof Pick<ClassificationCounts, 'pvs' | 'ps' | 'pm' | 'pp'>>
> = {
  pvs: {
    very_strong: 'pvs',
    strong: 'ps',
    moderate: 'pm',
    supporting: 'pp',
  },
  ps: {
    very_strong: 'ps',
    strong: 'ps',
    moderate: 'pm',
    supporting: 'pp',
  },
  pm: {
    very_strong: 'pm',
    strong: 'pm',
    moderate: 'pm',
    supporting: 'pp',
  },
  pp: {
    very_strong: 'pp',
    strong: 'pp',
    moderate: 'pp',
    supporting: 'pp',
  },
};

const addPathogenicCount = (
  acc: ClassificationCounts,
  code: string,
  strength: AppliedStrength,
) => {
  const family = getPathFamily(code);
  if (!family) return;
  const s =
    strength === 'standalone'
      ? ('supporting' as UserEvidenceStrength)
      : strength;
  const bucket = pathStrengthToBucket[family][s];
  acc[bucket] += 1;
};

const addBenignCount = (
  acc: ClassificationCounts,
  code: string,
  strength: AppliedStrength,
) => {
  if (code.startsWith('BA')) {
    if (strength === 'standalone') acc.ba += 1;
    else if (strength === 'strong') acc.bs += 1;
    else if (strength === 'moderate') acc.bs += 1;
    else acc.bp += 1;
    return;
  }
  if (code.startsWith('BS')) {
    if (strength === 'very_strong' || strength === 'strong') acc.bs += 1;
    else if (strength === 'moderate') acc.bs += 1;
    else acc.bp += 1;
    return;
  }
  if (code.startsWith('BP')) {
    acc.bp += 1;
  }
};

const getCountsFromSelections = (
  selections: Record<string, AppliedStrength>,
): ClassificationCounts => {
  const empty: ClassificationCounts = {
    pvs: 0,
    ps: 0,
    pm: 0,
    pp: 0,
    ba: 0,
    bs: 0,
    bp: 0,
  };
  return Object.entries(selections).reduce((acc, [code, strength]) => {
    const crit = criteria.find((c) => c.name === code);
    if (!crit) return acc;
    if (crit.bucket === 'pathogenic') addPathogenicCount(acc, code, strength);
    else addBenignCount(acc, code, strength);
    return acc;
  }, { ...empty });
};

export function strengthOptionsForCriterion(c: AcmgCriterion): AppliedStrength[] {
  if (c.bucket === 'benign' && c.name.startsWith('BA')) {
    return ['supporting', 'moderate', 'strong', 'standalone'];
  }
  return ['very_strong', 'strong', 'moderate', 'supporting'];
}

export function useAcmgClassification() {
  /** Applied criteria: code -> user-chosen strength */
  const [selections, setSelections] = useState<Record<string, AppliedStrength>>({});
  const [search, setSearch] = useState('');

  const filteredCriteria = useMemo(() => {
    if (!search.trim()) return criteria;
    const q = search.toLowerCase();
    return criteria.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q),
    );
  }, [search]);

  const counts = useMemo(() => getCountsFromSelections(selections), [selections]);
  const score = useMemo(() => getScoreFromSelections(selections), [selections]);
  const classification = useMemo(
    () => computeClassificationFromScore(score),
    [score],
  );

  const isApplied = useCallback(
    (code: string) => Object.prototype.hasOwnProperty.call(selections, code),
    [selections],
  );

  const setApplied = useCallback((code: string, applied: boolean) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (!applied) {
        delete next[code];
        return next;
      }
      const crit = criteria.find((c) => c.name === code);
      next[code] = crit?.defaultStrength ?? 'supporting';
      return next;
    });
  }, []);

  const setStrength = useCallback((code: string, strength: AppliedStrength) => {
    setSelections((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, code)) return prev;
      return { ...prev, [code]: strength };
    });
  }, []);

  const resetSelection = useCallback(() => setSelections({}), []);

  const appliedCount = useMemo(() => Object.keys(selections).length, [selections]);

  return {
    criteria,
    filteredCriteria,
    selections,
    search,
    setSearch,
    counts,
    score,
    classification,
    isApplied,
    setApplied,
    setStrength,
    resetSelection,
    appliedCount,
  };
}
